/** @jsxImportSource @emotion/react */
import { eq } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { FloorPlanAssembler, type FloorPlanAssemblerHandle } from '@kaiizen/kai-floor-plan-editor';
import type { AssemblyRoom, OpeningView } from '@kaiizen/kai-floor-plan-editor-core';
import { type AssemblyRoomPresentation, getAssemblyRooms } from '@kaiizen/kai-presenters';
import { getScanWithAssembledRooms, type ScanGroupOptimized } from '@kaiizen/kai-scan-template-system';
import { useCallback, useRef, useState } from 'react';

import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';
import { type ScanEditingContextType, useFeatureFlags, useScanEditingContextSelector, useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';

import { ANALYTICS_EVENTS, trackEvent } from '@/Client/analytics';
import { useEditorNavigationGuard } from '@/Client/State';

const ASSEMBLER_HOST_STYLE = css`
  label: FOCUSED_FLOOR_PLAN_ASSEMBLER_HOST_STYLE;
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  border-top: 1px solid ${COLORS.stroke};
`;

/** What the assembler's Copy JSON says about each room: the group it comes from, ahead of its floor plan. */
export const describeAssemblyGroup = (group: ScanGroupOptimized, scanId: string | undefined) => ({
  groupTemplateName: group.template.name,
  groupLabel: group.customLabel,
  scanId,
  groupId: group.id,
  groupTemplateId: group.template.id,
});

/** What the assembler draws from the scan: its rooms, and what each room's Copy JSON says about its group. */
interface AssemblyView {
  rooms: AssemblyRoomPresentation[];
  descriptions: Record<string, ReturnType<typeof describeAssemblyGroup>>;
}

/** The assembler's view of the scan, keeping the previous one while its rooms and descriptions are deeply unchanged. */
const selectAssemblyView = ({ scanRoot, scanId }: ScanEditingContextType, previous: AssemblyView | undefined): AssemblyView => {
  const rooms = getAssemblyRooms(scanRoot, previous?.rooms);
  const descriptions = Object.fromEntries(rooms.flatMap(({ id }) => {
    const group = scanRoot?.groupsById[id];
    return group ? [[id, describeAssemblyGroup(group, scanId)]] : [];
  }));
  return previous && previous.rooms === rooms && eq(previous.descriptions, descriptions) ? previous : { rooms, descriptions };
};

/**
 * Scope-level floor plan assembler: positions the property's rooms relative
 * to each other, level by level. Saves are ONE scan update covering exactly
 * the rooms whose placement changed; a read-only scan puts the assembler in
 * read-only mode. "Edit room" jumps to that group's floor plan editor, with
 * the view to open it in: 3D at the same angle when the floor plan shows 3D.
 * The `floorPlanEditorAdvanced` flag adds the whole-plan JSON (download and
 * copy) to the export menu.
 */
export const FocusedFloorPlanAssembler = ({ onOpenRoom }: {
  onOpenRoom?: (groupId: string, view: OpeningView) => void;
}) => {
  const { scanDataMutators, scanId, scanIsEditable } = useScanEditingContextSlice(['scanDataMutators', 'scanId', 'scanIsEditable']);
  const { featureFlags } = useFeatureFlags();
  const { rooms, descriptions } = useScanEditingContextSelector<AssemblyView>(selectAssemblyView);
  const describeRoom = useCallback(({ id }: AssemblyRoom) => descriptions[id] ?? { groupId: id }, [descriptions]);
  const assemblerRef = useRef<FloorPlanAssemblerHandle | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const saveRooms = useCallback(async (changedRooms: AssemblyRoom[]) => {
    if (!changedRooms.length) return;
    await scanDataMutators.setScan(currentScan => getScanWithAssembledRooms(currentScan, changedRooms));
    trackEvent(ANALYTICS_EVENTS.scanUpdated, { scanId, field: 'floor_plan_assembly', groupIds: changedRooms.map(room => room.id) });
  }, [scanDataMutators, scanId]);

  useEditorNavigationGuard(scanIsEditable && hasUnsavedChanges, assemblerRef);

  return (
    <div css={ASSEMBLER_HOST_STYLE} data-testid='focused-floor-plan-assembler'>
      <FloorPlanAssembler
        ref={assemblerRef}
        rooms={rooms}
        onSave={scanIsEditable ? saveRooms : undefined}
        onOpenRoom={onOpenRoom}
        onDirtyChange={setHasUnsavedChanges}
        readOnly={!scanIsEditable}
        describeRoom={describeRoom}
        advanced={featureFlags.floorPlanEditorAdvanced}
      />
    </div>
  );
};
